import { memo, useCallback } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import { useSmsBillingStore } from "../stores/smsBillingStore";
import { toMonthString, validateRange } from "../utils/month";

const monthInputClass =
  "h-7 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-700 tabular-nums outline-none hover:bg-gray-50 focus-visible:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50";

interface MonthRangeSelectorProps {
  /** 조회 중에는 범위를 바꾸거나 다시 조회할 수 없다 */
  disabled: boolean;
}

export const MonthRangeSelector = memo(function MonthRangeSelector({
  disabled,
}: MonthRangeSelectorProps) {
  const draftRange = useSmsBillingStore((s) => s.draftRange);
  const setDraftRange = useSmsBillingStore((s) => s.setDraftRange);
  const applyRange = useSmsBillingStore((s) => s.applyRange);
  const error = validateRange(draftRange);
  const maxMonth = toMonthString(new Date());

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraftRange({ ...draftRange, fromMonth: e.target.value }),
    [draftRange, setDraftRange],
  );
  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraftRange({ ...draftRange, toMonth: e.target.value }),
    [draftRange, setDraftRange],
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <input
          type="month"
          aria-label="시작월"
          value={draftRange.fromMonth}
          max={maxMonth}
          disabled={disabled}
          onChange={handleFromChange}
          className={monthInputClass}
        />
        <span className="text-xs text-gray-400">~</span>
        <input
          type="month"
          aria-label="종료월"
          value={draftRange.toMonth}
          max={maxMonth}
          disabled={disabled}
          onChange={handleToChange}
          className={monthInputClass}
        />
        <Button
          size="sm"
          disabled={disabled || error !== null}
          onClick={applyRange}
          className="ml-1.5 h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={Search01Icon} size={14} className="mr-1.5" />
          조회
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
});
